/**
 * Pestaña "Clientes" en el módulo Despacho.
 *
 * Muestra todos los clientes en una tabla con búsqueda y permite editarlos
 * desde el botón de la columna Acciones. La eliminación de clientes con
 * facturas asociadas está bloqueada por el backend.
 */
import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Pencil, Search, Users, Phone, MapPin, CalendarClock, UserPlus } from 'lucide-react';

export const ClientesTab = ({ clientes, onEdit, onCreate }) => {
  const [search, setSearch] = useState('');

  const filtrados = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return clientes;
    return clientes.filter((c) =>
      [c.nombre, c.telefono, c.direccion]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term)),
    );
  }, [clientes, search]);

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3">
        <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Space Grotesk' }}>
          <Users className="w-5 h-5" />
          Lista de Clientes ({filtrados.length}{search ? ` de ${clientes.length}` : ''})
        </CardTitle>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 w-full sm:w-[220px]"
              data-testid="search-cliente"
            />
          </div>
          {onCreate && (
            <Button size="sm" onClick={onCreate} data-testid="btn-nuevo-cliente-tab">
              <UserPlus className="w-4 h-4 mr-1.5" /> Nuevo
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {filtrados.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>{search ? 'No se encontraron clientes con ese término' : 'No hay clientes registrados'}</p>
            {!search && onCreate && (
              <Button variant="outline" size="sm" className="mt-3" onClick={onCreate}>
                <UserPlus className="w-4 h-4 mr-1.5" /> Crear primer cliente
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="tabla-clientes">
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead>Dirección</TableHead>
                  <TableHead className="text-center">Crédito</TableHead>
                  <TableHead className="text-right w-[110px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtrados.map((c) => {
                  const dc = Number(c.dias_credito || 0);
                  return (
                    <TableRow key={c.id || c._id} className="hover:bg-accent/60 transition-colors">
                      <TableCell className="font-medium">{c.nombre || '—'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {c.telefono ? (
                          <span className="inline-flex items-center gap-1.5">
                            <Phone className="w-3.5 h-3.5" /> {c.telefono}
                          </span>
                        ) : '—'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[260px] truncate" title={c.direccion || ''}>
                        {c.direccion ? (
                          <span className="inline-flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5 shrink-0" /> {c.direccion}
                          </span>
                        ) : '—'}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant={dc > 0 ? 'default' : 'secondary'} className="text-xs">
                          <CalendarClock className="w-3 h-3 mr-1" />
                          {dc === 0 ? 'Contado' : `${dc} día${dc === 1 ? '' : 's'}`}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEdit(c)}
                          title="Editar cliente"
                          data-testid={`btn-edit-cliente-${c.id || c._id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
